import {
    getVersionGoogle,
    getVersionInstagram,
    getVersionPopularity,
    getVersionTikTok,
} from 'utils/api/version.api';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';
import bugsnagClient from '@/ui/containers/BugsnagClient';
import dayjs from 'dayjs';
import { DATA_SOURCES } from '@/config/constants';
import { SOURCES } from 'utils/dataSources';
import { useQueries } from 'react-query';

dayjs.extend(AdvancedFormat);

const shapeDbDates = (dbDates) => {
    const endDateByCountry = {};
    for (const country in dbDates) {
        const shapedDates = {};
        const [cutoffDate, refreshYear, refreshMonth] = dbDates[country] || [];

        const endDate = dayjs(cutoffDate || null);
        if (endDate.isValid()) {
            shapedDates.endDate = endDate.format('MMMM Do, YYYY');
        } else {
            shapedDates.endDate = null;
            bugsnagClient.notify(
                new Error(`Could not create valid date from "${cutoffDate}"`),
            );
        }

        const sourceEndDate = dayjs()
            .set('year', refreshYear)
            .set('month', refreshMonth - 1);
        if (sourceEndDate.isValid()) {
            shapedDates.sourceEndDate = sourceEndDate.format('YYYY-MM');
        } else {
            shapedDates.sourceEndDate = null;
            bugsnagClient.notify(
                new Error(
                    `Could not create valid date from refreshYear "${refreshYear}" and refreshMonth "${refreshMonth}"`,
                ),
            );
        }

        endDateByCountry[country] = shapedDates;
    }
    return endDateByCountry;
};

const queryOptions = { cacheTime: Infinity, staleTime: Infinity };

/**
 * Which endpoint answers for each source. Held here rather than on the source
 * descriptor so that stays a leaf with no api imports.
 */
export const VERSION_QUERY_FN = {
    [DATA_SOURCES.GOOGLE]: () =>
        getVersionGoogle().then(({ dbDates, ...rest }) => ({
            dbDates: shapeDbDates(dbDates),
            ...rest,
        })),
    [DATA_SOURCES.TIKTOK]: getVersionTikTok,
    [DATA_SOURCES.INSTAGRAM]: getVersionInstagram,
    [DATA_SOURCES.POPULARITY]: getVersionPopularity,
};

/**
 * Keyed on the descriptor's own declaration rather than on `VERSION_QUERY_FN`:
 * a source with no version endpoint opts out by omitting the version keys.
 */
const VERSIONED_SOURCES = Object.values(SOURCES).filter(
    ({ versionResultKey }) => versionResultKey,
);

// Declaring the version keys but registering no endpoint is a wiring bug, and
// it is silent at every other layer: the query would run without a `queryFn`,
// settle to an error result, and look like a failed request. Throwing as the
// module loads turns it into an import-time failure, so the dev server, the
// production build's module init, and any test that touches this file all
// surface it immediately rather than at the moment a page renders.
const UNREGISTERED = VERSIONED_SOURCES.filter(
    ({ key }) => !VERSION_QUERY_FN[key],
);
if (UNREGISTERED.length) {
    throw new Error(
        `No version query registered for: ${UNREGISTERED.map(({ key }) => key).join(', ')}`,
    );
}

export const useDataSourceVersion = () => {
    const results = useQueries(
        VERSIONED_SOURCES.map(({ key, versionQueryKey }) => ({
            queryKey: versionQueryKey,
            queryFn: VERSION_QUERY_FN[key],
            ...queryOptions,
        })),
    );
    return Object.fromEntries(
        VERSIONED_SOURCES.map(({ versionResultKey }, idx) => [
            versionResultKey,
            results[idx],
        ]),
    );
};
